import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert, Divider,
  InputAdornment, IconButton, Chip
} from '@mui/material';
import { Visibility, VisibilityOff, Search, Save, Lock, Person, Business } from '@mui/icons-material';
import CustomerLayout from '../components/CustomerLayout';
import { customerAuthAPI } from '../services/api';

const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};

export default function MyAccountPage({ onLogout }) {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    name: '', company_name: '', email: '', office_phone: '',
    address: '', business_number: '', ceo_name: '', start_dt: '',
  });
  const [emailOtp, setEmailOtp] = useState({ code: '', sent: false, verified: false, sending: false, verifying: false });

  // 사업자번호 000-00-00000 포맷
  const formatBizNo = (value) => {
    const d = (value || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,10)}`;
  };
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [snack, setSnack] = useState({ type: '', msg: '' });
  const [pwSnack, setPwSnack] = useState({ type: '', msg: '' });
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    customerAuthAPI.me().then((res) => {
      const d = res.data?.data;
      if (!d) return;
      setMe(d);
      setForm({
        name: d.name || '',
        company_name: d.company_name || '',
        email: d.email || '',
        office_phone: formatPhone(d.office_phone || ''),
        address: d.address || '',
        business_number: formatBizNo(d.business_number || ''),
        ceo_name: d.ceo_name || '',
        start_dt: '',
      });
    }).catch(() => {});
  }, []);

  const openAddressSearch = () => {
    if (!window.daum || !window.daum.Postcode) return alert('주소 검색 서비스를 불러오는 중입니다.');
    new window.daum.Postcode({
      oncomplete: (data) => setForm((p) => ({ ...p, address: data.roadAddress || data.jibunAddress })),
    }).open();
  };

  const emailChanged = me && (form.email || '') !== (me.email || '');
  const handleSendOtp = async () => {
    if (!form.email) { setSnack({ type: 'error', msg: '이메일을 먼저 입력하세요.' }); return; }
    setEmailOtp((p) => ({ ...p, sending: true }));
    try {
      await customerAuthAPI.sendEmailOtp(form.email);
      setEmailOtp((p) => ({ ...p, sent: true, sending: false }));
      setSnack({ type: 'success', msg: '인증 코드를 메일로 전송했습니다 (10분 안에 입력).' });
    } catch (e) {
      setEmailOtp((p) => ({ ...p, sending: false }));
      setSnack({ type: 'error', msg: e.response?.data?.message || '인증코드 전송 실패' });
    }
  };
  const handleVerifyOtp = async () => {
    setEmailOtp((p) => ({ ...p, verifying: true }));
    try {
      await customerAuthAPI.verifyEmailOtp(form.email, emailOtp.code);
      setEmailOtp((p) => ({ ...p, verified: true, verifying: false }));
      setSnack({ type: 'success', msg: '이메일이 인증되었습니다.' });
    } catch (e) {
      setEmailOtp((p) => ({ ...p, verifying: false }));
      setSnack({ type: 'error', msg: e.response?.data?.message || '인증 실패' });
    }
  };

  const handleSave = async () => {
    setSaving(true); setSnack({ type: '', msg: '' });
    if (emailChanged && form.email && !emailOtp.verified) {
      setSnack({ type: 'error', msg: '이메일이 변경되었습니다. 인증코드를 받아 확인해 주세요.' });
      setSaving(false); return;
    }
    try {
      await customerAuthAPI.updateMe(form);
      setSnack({ type: 'success', msg: '저장되었습니다.' });
      setEmailOtp({ code: '', sent: false, verified: false, sending: false, verifying: false });
    } catch (e) {
      setSnack({ type: 'error', msg: e.response?.data?.message || '저장 실패' });
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    setPwSnack({ type: '', msg: '' });
    if (!pwForm.current_password || !pwForm.new_password) return setPwSnack({ type: 'error', msg: '현재 비밀번호와 새 비밀번호를 입력하세요.' });
    if (pwForm.new_password.length < 6) return setPwSnack({ type: 'error', msg: '새 비밀번호는 6자 이상이어야 합니다.' });
    if (pwForm.new_password !== pwForm.confirm) return setPwSnack({ type: 'error', msg: '새 비밀번호가 일치하지 않습니다.' });
    setPwSaving(true);
    try {
      await customerAuthAPI.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwSnack({ type: 'success', msg: '비밀번호가 변경되었습니다.' });
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      setPwSnack({ type: 'error', msg: e.response?.data?.message || '비밀번호 변경 실패' });
    } finally { setPwSaving(false); }
  };

  const isCorporate = me?.customer_type === 'corporate';

  return (
    <CustomerLayout onLogout={onLogout}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>내 정보</Typography>
        {me && (
          <Chip
            icon={isCorporate ? <Business /> : <Person />}
            label={isCorporate ? '기업 고객' : '개인'}
            color={isCorporate ? 'primary' : 'default'}
            size="small"
          />
        )}
        {me?.phone && <Typography variant="body2" color="text.secondary">· {formatPhone(me.phone)}</Typography>}
      </Box>

      {/* 개인정보 수정 */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>개인정보 수정</Typography>
        {snack.msg && <Alert severity={snack.type || 'info'} sx={{ mb: 2 }} onClose={() => setSnack({ type: '', msg: '' })}>{snack.msg}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="이름 *" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="연락처 (변경 불가)" value={formatPhone(me?.phone || '')} disabled />
          </Grid>

          {isCorporate && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">기업 정보</Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="업체명 *" value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="대표자명" value={form.ceo_name}
                  onChange={(e) => setForm({ ...form, ceo_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="사업자번호" value={form.business_number}
                  onChange={(e) => setForm({ ...form, business_number: formatBizNo(e.target.value) })}
                  inputProps={{ maxLength: 12 }}
                  placeholder="000-00-00000" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="개업일자 (사업자정보 변경 시 필요)" type="date"
                  value={form.start_dt}
                  onChange={(e) => setForm({ ...form, start_dt: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="사업자번호·대표·업체명 중 하나라도 바꾸면 국세청 진위확인 필요" />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField fullWidth label={emailChanged && !emailOtp.verified ? "E-Mail (인증 필요)" : "E-Mail"} type="email" value={form.email}
                    onChange={(e) => { setForm({ ...form, email: e.target.value }); setEmailOtp({ code: '', sent: false, verified: false, sending: false, verifying: false }); }}
                    placeholder="예: contact@company.co.kr"
                    error={emailChanged && !emailOtp.verified}
                  />
                  {emailChanged && !emailOtp.verified && (
                    <Button variant="outlined" sx={{ whiteSpace: 'nowrap', height: 56 }} disabled={emailOtp.sending || !form.email} onClick={handleSendOtp}>
                      {emailOtp.sending ? '전송중' : (emailOtp.sent ? '재전송' : '인증코드')}
                    </Button>
                  )}
                  {emailOtp.verified && (
                    <Box sx={{ color: 'success.main', fontSize: 13, alignSelf: 'center', whiteSpace: 'nowrap' }}>✓ 인증됨</Box>
                  )}
                </Box>
                {emailOtp.sent && !emailOtp.verified && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField fullWidth size="small" label="인증 코드 (6자리)"
                      value={emailOtp.code}
                      onChange={(e) => setEmailOtp((p) => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      inputProps={{ maxLength: 6 }}
                    />
                    <Button variant="contained" sx={{ whiteSpace: 'nowrap' }} disabled={emailOtp.verifying || !emailOtp.code} onClick={handleVerifyOtp}>
                      {emailOtp.verifying ? '확인중' : '확인'}
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="사무실 연락처" value={form.office_phone}
                  onChange={(e) => setForm({ ...form, office_phone: formatPhone(e.target.value) })}
                  placeholder="02-1234-5678" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="주소" value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" variant="outlined" startIcon={<Search />}
                          onClick={openAddressSearch} sx={{ whiteSpace: 'nowrap' }}>검색</Button>
                      </InputAdornment>
                    )
                  }} />
              </Grid>
            </>
          )}

          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 비밀번호 변경 */}
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>비밀번호 변경</Typography>
        {pwSnack.msg && <Alert severity={pwSnack.type || 'info'} sx={{ mb: 2 }} onClose={() => setPwSnack({ type: '', msg: '' })}>{pwSnack.msg}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="현재 비밀번호" type={showPw ? 'text' : 'password'}
              value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPw(!showPw)} edge="end">
                      {showPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="새 비밀번호 (6자 이상)" type="password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="새 비밀번호 확인" type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
          </Grid>
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <Button variant="contained" color="secondary" startIcon={<Lock />} onClick={handleChangePassword} disabled={pwSaving}>
              {pwSaving ? '변경 중...' : '비밀번호 변경'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </CustomerLayout>
  );
}
